// In hooks/useChatSession.js
import { useState, useCallback, useEffect } from "react";
import useUserPlan from "./useUserPlan";

const API_URL = "https://vintariai-backend-1.onrender.com";

export default function useChatSession(supabase, user) {
  const userPlan = useUserPlan();    
  const [chatList, setChatList] = useState([]);
  const [conversation, setConversation] = useState({ id: null, messages: [] });

  const fetchChatSessions = useCallback(async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from("chat_sessions")
        .select("id, title")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false }); // Show newest first

      if (error) throw error;
      setChatList(data || []);
    } catch (err) {
      console.error("❌ Error fetching chat sessions:", err);
      setChatList([]);
    }
  }, [supabase, user]);

  const fetchChatHistory = useCallback(async (sessionId) => {
    // If there's no session or user is on free plan, ensure UI is clean.
    if (!user || !sessionId || userPlan === "free") {
      setConversation({ id: sessionId || null, messages: [] });
      return;
    }

    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      if (!token) throw new Error("No auth token found");

      const res = await fetch(`${API_URL}/api/history/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error(`Fetch failed with status ${res.status}`);
      const data = await res.json();
      
      const formattedMessages = Array.isArray(data.messages) 
        ? data.messages.flatMap(msg => [
            msg.prompt ? { role: "user", content: msg.prompt } : null,
            msg.response ? { role: "assistant", content: msg.response } : null,
          ]).filter(Boolean)
        : [];
      
      const newConversation = { id: sessionId, messages: formattedMessages };
      setConversation(newConversation);
    } catch (err) {
      console.error("❌ Error loading chat history:", err.message);
      setConversation({ id: sessionId, messages: [] });
    }
  }, [supabase, user, userPlan]);

  // This effect runs once on initial load to restore the last active session
  useEffect(() => {
    const lastChatId = localStorage.getItem("vintari_current_chat_id");
    if (lastChatId) {
      fetchChatHistory(lastChatId);
    }
    fetchChatSessions();
}, [fetchChatHistory, fetchChatSessions]); // ✅ Add it here

  return {
    chatList,
    conversation,
    fetchChatSessions,
    fetchChatHistory,
    setConversation, // Return the standard setter
  };
}